import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Table } from "../../../components/SystemComponent/table";
import { GroupButton } from "../../../components/SystemComponent/GroupButton";
import { Badge } from "../../../components/SystemComponent/Bagde";
import { Modal } from "../../../components/SystemComponent/Modal";
import { Button } from "../../../components/SystemComponent/Button";
import {
  MdVisibility,
  MdCheck,
  MdClose,
  MdGavel,
  MdBusiness,
  MdPerson,
  MdInfo,
  MdDescription,
  MdAttachMoney,
  MdSchedule,
  MdEdit,
  MdFlight,
  MdSettings,
  MdUndo,
  MdHistory,
  MdStar,
  MdPlaylistAddCheck,
} from "react-icons/md";
import { createApi } from "../../../../core/api";

const TENDER_STATUS: Record<number, { label: string; color: string; bg: string }> = {
  0:  { label: "Draft",              color: "#6B7280", bg: "rgba(107,114,128,.15)" },
  1:  { label: "Pending Admin",      color: "#F59E0B", bg: "rgba(245,158,11,.15)"  },
  2:  { label: "Returned",           color: "#F97316", bg: "rgba(249,115,22,.15)"  },
  3:  { label: "Rejected",           color: "#DC2626", bg: "rgba(220,38,38,.15)"   },
  4:  { label: "Open",               color: "#16A34A", bg: "rgba(22,163,74,.15)"   },
  5:  { label: "Shortlisting",       color: "#0EA5E9", bg: "rgba(14,165,233,.15)"  },
  6:  { label: "Expired",            color: "#F97316", bg: "rgba(249,115,22,.15)"  },
  7:  { label: "Partially Awarded",  color: "#8B5CF6", bg: "rgba(139,92,246,.15)"  },
  8:  { label: "Awarded",            color: "#7C3AED", bg: "rgba(124,58,237,.15)"  },
  9:  { label: "Closed",             color: "#374151", bg: "rgba(55,65,81,.15)"    },
  10: { label: "Closed (No Award)",  color: "#6B7280", bg: "rgba(107,114,128,.15)" },
  11: { label: "Cancelled",          color: "#DC2626", bg: "rgba(220,38,38,.15)"   },
};

const OFFER_STATUS: Record<number, { label: string; color: string }> = {
  0: { label: "Draft",     color: "#6B7280" },
  1: { label: "Submitted", color: "#16A34A" },
  2: { label: "Withdrawn", color: "#DC2626" },
  3: { label: "Restored",  color: "#0EA5E9" },
};

const DIRECTION: Record<number, string> = { 0: "Buy", 1: "Sell" };
const VISIBILITY: Record<number, string> = { 0: "Public", 1: "Invitation Only" };

const fmt = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const fmtDateTime = (d?: string | null) =>
  d ? new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const Loader = () => (
  <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
    <div
      style={{
        width: 42, height: 42,
        border: "4px solid #CBD5E1", borderTopColor: "#2563EB",
        borderRadius: "50%", animation: "spin 1s linear infinite",
      }}
    />
  </div>
);

const StatusBadge = ({ status }: { status: number }) => {
  const s = TENDER_STATUS[status] ?? TENDER_STATUS[0];
  return <Badge label={s.label} color={s.color} bg={s.bg} />;
};

const InfoRow = ({ icon: Icon, label, value }: any) => (
  <div style={{
    display: "grid", gridTemplateColumns: "26px 170px 1fr",
    gap: 10, padding: "6px 0", borderBottom: "1px solid #E5E7EB", fontSize: 12,
  }}>
    <Icon size={18} color="#2563EB" />
    <span style={{ color: "#64748B", fontWeight: 600 }}>{label}</span>
    <span style={{ fontWeight: 500 }}>{value ?? "—"}</span>
  </div>
);

const SectionCard = ({ icon: Icon, title, children, color = "#3B82F6" }: any) => (
  <div style={{ border: "1px solid #E5E7EB", borderRadius: 14, padding: 16, background: "#FFFFFFAA" }}>
    <div style={{
      display: "flex", alignItems: "center", gap: 8, marginBottom: 14,
      background: `linear-gradient(135deg,${color} 0%,${color}CC 100%)`,
      color: "#fff", borderRadius: 8, padding: "6px 10px",
    }}>
      <Icon size={18} />
      <strong style={{ fontSize: 13 }}>{title}</strong>
    </div>
    {children}
  </div>
);

const StatCard = ({ label, value, color }: { label: string; value: number; color: string }) => (
  <div style={{
    flex: "1 1 120px", background: "#fff", border: `2px solid ${color}33`,
    borderRadius: 12, padding: "14px 18px", display: "flex", flexDirection: "column", gap: 4,
  }}>
    <span style={{ fontSize: 26, fontWeight: 700, color }}>{value}</span>
    <span style={{ fontSize: 11, color: "#64748B" }}>{label}</span>
  </div>
);

const TabButton = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) => (
  <button
    onClick={onClick}
    style={{
      padding: "8px 16px", borderRadius: 8, border: "1px solid #D1D5DB",
      background: active ? "#2563EB" : "#fff", color: active ? "#fff" : "#374151",
      fontWeight: 600, fontSize: 13, cursor: "pointer",
    }}
  >
    {children}
  </button>
);

// ---- Reject/Return Modal ----
const ReasonModal = ({ title, onClose, onSubmit }: { title: string; onClose: () => void; onSubmit: (reason: string) => void }) => {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  return (
    <Modal open title={title} onClose={onClose}>
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 12, color: "#64748B", display: "block", marginBottom: 6 }}>Reason</label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={4}
          style={{ width: "100%", borderRadius: 8, border: "1px solid #D1D5DB", padding: 10, fontSize: 13 }}
          placeholder="Enter reason..."
        />
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <Button icon={MdCheck} loading={saving} onClick={async () => {
          if (!reason.trim()) return;
          setSaving(true);
          await onSubmit(reason);
          setSaving(false);
        }}>Submit</Button>
        <Button icon={MdClose} variant="ghost" onClick={onClose}>Cancel</Button>
      </div>
    </Modal>
  );
};

// ---- Tender Detail Modal ----
const TenderDetailModal = ({ id, onClose }: { id: number; onClose: () => void }) => {
  const api = useMemo(() => createApi(), []);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"detail" | "offers" | "shortlist" | "awards" | "audit">("detail");
  const [offers, setOffers] = useState<any[]>([]);
  const [shortlist, setShortlist] = useState<any[]>([]);
  const [awards, setAwards] = useState<any[]>([]);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [tabLoading, setTabLoading] = useState(false);

  useEffect(() => {
    api.adminTender.getById(id).then((r: any) => {
      setData(r?.data?.data);
      setLoading(false);
    });
  }, [id]);

  const loadTab = useCallback(async (t: string) => {
    setTabLoading(true);
    if (t === "offers") {
      const r = await api.adminTender.getOffers(id);
      setOffers((r as any)?.data?.data ?? []);
    } else if (t === "shortlist") {
      const r = await api.adminTender.getShortlist(id);
      setShortlist((r as any)?.data?.data ?? []);
    } else if (t === "awards") {
      const r = await api.adminTender.getAwards(id);
      setAwards((r as any)?.data?.data ?? []);
    } else if (t === "audit") {
      const r = await api.adminTender.getAuditLog(id);
      setAuditLogs((r as any)?.data?.data ?? []);
    }
    setTabLoading(false);
  }, [id]);

  useEffect(() => {
    if (tab !== "detail") loadTab(tab);
  }, [tab, loadTab]);

  return (
    <Modal open fullScreen onClose={onClose}>
      {loading ? (
        <Loader />
      ) : !data ? (
        <div style={{ padding: 40, textAlign: "center", color: "#DC2626" }}>Tender not found.</div>
      ) : (
        <div style={{ padding: 24 }}>
          {/* Header */}
          <div style={{ marginBottom: 20 }}>
            <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>{data.title}</h1>
            <div style={{ display: "flex", gap: 10, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
              <StatusBadge status={data.status} />
              <Badge label={DIRECTION[data.direction] ?? "—"} color="#6B7280" bg="rgba(107,114,128,.1)" />
              <Badge label={VISIBILITY[data.visibility] ?? "—"} color="#6B7280" bg="rgba(107,114,128,.1)" />
              <span style={{ fontSize: 12, color: "#94A3B8" }}>#{data.id}</span>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
            <TabButton active={tab === "detail"} onClick={() => setTab("detail")}>Details</TabButton>
            <TabButton active={tab === "offers"} onClick={() => setTab("offers")}>Offers ({data.offerCount ?? 0})</TabButton>
            <TabButton active={tab === "shortlist"} onClick={() => setTab("shortlist")}>Shortlist</TabButton>
            <TabButton active={tab === "awards"} onClick={() => setTab("awards")}>Awards</TabButton>
            <TabButton active={tab === "audit"} onClick={() => setTab("audit")}>Audit Log</TabButton>
          </div>

          {/* Tab: Detail */}
          {tab === "detail" && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(400px,1fr))", gap: 20 }}>
              <SectionCard icon={MdDescription} title="Tender Details" color="#3B82F6">
                <InfoRow icon={MdDescription} label="Title" value={data.title} />
                <InfoRow icon={MdInfo} label="Description" value={data.description} />
                <InfoRow icon={MdInfo} label="Direction" value={DIRECTION[data.direction] ?? "—"} />
                <InfoRow icon={MdInfo} label="Transaction Type" value={data.transactionType} />
                <InfoRow icon={MdInfo} label="Visibility" value={VISIBILITY[data.visibility] ?? "—"} />
                <InfoRow icon={MdAttachMoney} label="Currency" value={data.currencyCode} />
                {data.adminReturnReason && <InfoRow icon={MdUndo} label="Return Reason" value={data.adminReturnReason} />}
                {data.adminRejectReason && <InfoRow icon={MdClose} label="Reject Reason" value={data.adminRejectReason} />}
                {data.closeReason && <InfoRow icon={MdInfo} label="Close Reason" value={data.closeReason} />}
              </SectionCard>

              <SectionCard icon={MdBusiness} title="Issuing Company" color="#7C3AED">
                <InfoRow icon={MdBusiness} label="Company" value={data.companyName} />
                <InfoRow icon={MdPerson} label="Created By" value={data.userFullName} />
                <InfoRow icon={MdFlight} label="Module" value={data.moduleName} />
                <InfoRow icon={MdInfo} label="Module Code" value={data.moduleCode} />
              </SectionCard>

              <SectionCard icon={MdSchedule} title="Timeline" color="#F97316">
                <InfoRow icon={MdSchedule} label="Bid Deadline" value={fmtDateTime(data.bidDeadlineUtc)} />
                <InfoRow icon={MdSchedule} label="Created Date" value={fmt(data.createdDate)} />
                <InfoRow icon={MdSchedule} label="Published At" value={fmtDateTime(data.publishedAtUtc)} />
                <InfoRow icon={MdSchedule} label="Closed At" value={fmtDateTime(data.closedAtUtc)} />
                <InfoRow icon={MdInfo} label="Remaining" value={data.remainingSeconds > 0 ? `${Math.floor(data.remainingSeconds / 3600)}h ${Math.floor((data.remainingSeconds % 3600) / 60)}m` : "Expired"} />
              </SectionCard>

              <SectionCard icon={MdGavel} title="Statistics" color="#16A34A">
                <InfoRow icon={MdGavel} label="Offers" value={data.offerCount ?? 0} />
                <InfoRow icon={MdVisibility} label="Views" value={data.viewCount ?? 0} />
                <InfoRow icon={MdInfo} label="Schedule Alternative" value={data.allowScheduleAlternative ? "Yes" : "No"} />
              </SectionCard>

              {/* Requirement Lines */}
              {(data.requirementLines ?? []).length > 0 && (
                <SectionCard icon={MdPlaylistAddCheck} title={`Requirements (${data.requirementLines.length})`} color="#0EA5E9">
                  {data.requirementLines.map((r: any, i: number) => (
                    <InfoRow key={i} icon={MdInfo} label={r.label || r.key} value={r.value} />
                  ))}
                </SectionCard>
              )}

              {/* Aircraft Assets */}
              {(data.aircraftAssets ?? []).length > 0 && (
                <SectionCard icon={MdFlight} title={`Aircraft Assets (${data.aircraftAssets.length})`} color="#0EA5E9">
                  {data.aircraftAssets.map((a: any, i: number) => (
                    <div key={i} style={{ padding: "8px 0", borderBottom: i < data.aircraftAssets.length - 1 ? "1px solid #F1F5F9" : "none" }}>
                      <InfoRow icon={MdFlight} label="MSN" value={a.msn} />
                      <InfoRow icon={MdInfo} label="Registration" value={a.registration} />
                      <InfoRow icon={MdInfo} label="YOM" value={a.yearOfManufacture} />
                      <InfoRow icon={MdInfo} label="Hours/Cycles" value={`${a.totalFlightHours ?? 0}h / ${a.totalCycles ?? 0}c`} />
                      {a.askingPrice && <InfoRow icon={MdAttachMoney} label="Asking Price" value={`${a.priceCurrencyCode ?? ""} ${Number(a.askingPrice).toLocaleString()}`} />}
                    </div>
                  ))}
                </SectionCard>
              )}

              {/* Engine Assets */}
              {(data.engineAssets ?? []).length > 0 && (
                <SectionCard icon={MdSettings} title={`Engine Assets (${data.engineAssets.length})`} color="#6B7280">
                  {data.engineAssets.map((e: any, i: number) => (
                    <div key={i} style={{ padding: "8px 0", borderBottom: i < data.engineAssets.length - 1 ? "1px solid #F1F5F9" : "none" }}>
                      <InfoRow icon={MdSettings} label="ESN" value={e.esn} />
                      <InfoRow icon={MdInfo} label="Condition" value={e.condition} />
                      <InfoRow icon={MdInfo} label="Hours/Cycles" value={`${e.totalFlightHours ?? 0}h / ${e.totalCycles ?? 0}c`} />
                      {e.askingPrice && <InfoRow icon={MdAttachMoney} label="Asking Price" value={`${e.priceCurrencyCode ?? ""} ${Number(e.askingPrice).toLocaleString()}`} />}
                    </div>
                  ))}
                </SectionCard>
              )}

              {/* Attachments */}
              {(data.attachments ?? []).length > 0 && (
                <SectionCard icon={MdDescription} title={`Attachments (${data.attachments.length})`} color="#F97316">
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                    {data.attachments.map((a: any) => (
                      <a
                        key={a.id}
                        href={a.fileUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                          padding: 12, border: "1px solid #E5E7EB", borderRadius: 10,
                          textDecoration: "none", color: "#374151", fontSize: 11,
                          background: "#F8FAFC", width: 90,
                        }}
                      >
                        <MdDescription size={30} color="#F97316" />
                        <span style={{ wordBreak: "break-all", textAlign: "center" }}>{a.fileName}</span>
                      </a>
                    ))}
                  </div>
                </SectionCard>
              )}
            </div>
          )}

          {/* Tab: Offers */}
          {tab === "offers" && (
            tabLoading ? <Loader /> : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "#F8FAFC" }}>
                      {["#", "Company", "Status", "Rev.", "Targets", "Submitted", "Viewed"].map(h => (
                        <th key={h} style={{ padding: "8px 12px", textAlign: "left", borderBottom: "1px solid #E5E7EB", color: "#64748B" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {offers.length === 0 ? (
                      <tr><td colSpan={7} style={{ padding: 24, textAlign: "center", color: "#94A3B8" }}>No offers yet.</td></tr>
                    ) : offers.map((o: any) => {
                      const os = OFFER_STATUS[o.status] ?? { label: String(o.status), color: "#6B7280" };
                      return (
                        <tr key={o.id} style={{ borderBottom: "1px solid #F1F5F9" }}>
                          <td style={{ padding: "8px 12px", color: "#94A3B8" }}>#{o.id}</td>
                          <td style={{ padding: "8px 12px", fontWeight: 600 }}>{o.bidderCompanyName}</td>
                          <td style={{ padding: "8px 12px" }}>
                            <Badge label={os.label} color={os.color} bg={`${os.color}22`} />
                          </td>
                          <td style={{ padding: "8px 12px", color: "#64748B" }}>v{o.revisionNumber}</td>
                          <td style={{ padding: "8px 12px", color: "#64748B" }}>{o.targetCount}</td>
                          <td style={{ padding: "8px 12px", color: "#64748B" }}>{fmtDateTime(o.submittedAtUtc)}</td>
                          <td style={{ padding: "8px 12px", color: "#64748B" }}>{fmtDateTime(o.viewedAtUtc)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* Tab: Shortlist */}
          {tab === "shortlist" && (
            tabLoading ? <Loader /> : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "#F8FAFC" }}>
                      {["#", "Company", "Target Type", "Active", "Shortlisted At", "Removal Reason"].map(h => (
                        <th key={h} style={{ padding: "8px 12px", textAlign: "left", borderBottom: "1px solid #E5E7EB", color: "#64748B" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {shortlist.length === 0 ? (
                      <tr><td colSpan={6} style={{ padding: 24, textAlign: "center", color: "#94A3B8" }}>No shortlist entries.</td></tr>
                    ) : shortlist.map((s: any) => (
                      <tr key={s.id} style={{ borderBottom: "1px solid #F1F5F9" }}>
                        <td style={{ padding: "8px 12px", color: "#94A3B8" }}>#{s.id}</td>
                        <td style={{ padding: "8px 12px", fontWeight: 600 }}>{s.companyName}</td>
                        <td style={{ padding: "8px 12px" }}>{s.targetType}</td>
                        <td style={{ padding: "8px 12px" }}>
                          <Badge label={s.isActive ? "Active" : "Removed"} color={s.isActive ? "#16A34A" : "#DC2626"} bg={s.isActive ? "rgba(22,163,74,.12)" : "rgba(220,38,38,.12)"} />
                        </td>
                        <td style={{ padding: "8px 12px", color: "#64748B" }}>{fmtDateTime(s.shortlistedAtUtc)}</td>
                        <td style={{ padding: "8px 12px", color: "#64748B" }}>{s.removalReason ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* Tab: Awards */}
          {tab === "awards" && (
            tabLoading ? <Loader /> : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "#F8FAFC" }}>
                      {["#", "Company", "Target Type", "Awarded At", "Notes"].map(h => (
                        <th key={h} style={{ padding: "8px 12px", textAlign: "left", borderBottom: "1px solid #E5E7EB", color: "#64748B" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {awards.length === 0 ? (
                      <tr><td colSpan={5} style={{ padding: 24, textAlign: "center", color: "#94A3B8" }}>No awards yet.</td></tr>
                    ) : awards.map((a: any) => (
                      <tr key={a.id} style={{ borderBottom: "1px solid #F1F5F9" }}>
                        <td style={{ padding: "8px 12px", color: "#94A3B8" }}>#{a.id}</td>
                        <td style={{ padding: "8px 12px", fontWeight: 600 }}>{a.companyName}</td>
                        <td style={{ padding: "8px 12px" }}>{a.targetType}</td>
                        <td style={{ padding: "8px 12px", color: "#64748B" }}>{fmtDateTime(a.awardedAtUtc)}</td>
                        <td style={{ padding: "8px 12px", color: "#64748B" }}>{a.notes ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* Tab: Audit Log */}
          {tab === "audit" && (
            tabLoading ? <Loader /> : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "#F8FAFC" }}>
                      {["#", "Action", "User ID", "Company ID", "Notes", "Occurred At"].map(h => (
                        <th key={h} style={{ padding: "8px 12px", textAlign: "left", borderBottom: "1px solid #E5E7EB", color: "#64748B" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.length === 0 ? (
                      <tr><td colSpan={6} style={{ padding: 24, textAlign: "center", color: "#94A3B8" }}>No audit logs.</td></tr>
                    ) : auditLogs.map((l: any) => (
                      <tr key={l.id} style={{ borderBottom: "1px solid #F1F5F9" }}>
                        <td style={{ padding: "8px 12px", color: "#94A3B8" }}>#{l.id}</td>
                        <td style={{ padding: "8px 12px", fontWeight: 600 }}>{l.action}</td>
                        <td style={{ padding: "8px 12px" }}>{l.actorUserId ?? "—"}</td>
                        <td style={{ padding: "8px 12px" }}>{l.actorCompanyId ?? "—"}</td>
                        <td style={{ padding: "8px 12px", color: "#64748B" }}>{l.notes ?? "—"}</td>
                        <td style={{ padding: "8px 12px", color: "#64748B" }}>{fmtDateTime(l.occurredAtUtc)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      )}
    </Modal>
  );
};

// ---- Main Page ----
export const AdminTenderList = () => {
  const api = useMemo(() => createApi(), []);

  const [data, setData] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const [filterStatus, setFilterStatus] = useState<string>("");
  const [filterModule, setFilterModule] = useState<string>("");

  const [detailId, setDetailId] = useState<number | null>(null);
  const [reasonModal, setReasonModal] = useState<{ id: number; type: "reject" | "return" } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [listRes, statsRes] = await Promise.all([
      api.adminTender.getAll(),
      api.adminTender.getStats(),
    ]);
    setData((listRes as any)?.data?.data ?? []);
    setStats((statsRes as any)?.data?.data ?? null);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const moduleOptions = useMemo(() => {
    const seen = new Set<string>();
    data.forEach((r) => { if (r.moduleName) seen.add(r.moduleName); });
    return Array.from(seen).sort();
  }, [data]);

  const filtered = useMemo(() =>
    data.filter((r) => {
      if (filterStatus !== "" && String(r.status) !== filterStatus) return false;
      if (filterModule !== "" && r.moduleName !== filterModule) return false;
      return true;
    }),
    [data, filterStatus, filterModule]
  );

  const columns = useMemo(() => [
    { key: "id", title: "#", accessor: (r: any) => r.id },
    { key: "title", title: "Title", accessor: (r: any) => r.title },
    { key: "module", title: "Module", accessor: (r: any) => r.moduleName },
    { key: "company", title: "Company", accessor: (r: any) => r.companyName },
    { key: "createdBy", title: "Created By", accessor: (r: any) => r.userFullName },
    {
      key: "status", title: "Status",
      render: (r: any) => <StatusBadge status={r.status} />,
    },
    {
      key: "direction", title: "Direction",
      render: (r: any) => (
        <Badge
          label={DIRECTION[r.direction] ?? "—"}
          color={r.direction === 0 ? "#16A34A" : "#7C3AED"}
          bg={r.direction === 0 ? "rgba(22,163,74,.12)" : "rgba(124,58,237,.12)"}
        />
      ),
    },
    { key: "offers", title: "Offers", accessor: (r: any) => r.offerCount },
    { key: "views", title: "Views", accessor: (r: any) => r.viewCount },
    {
      key: "deadline", title: "Deadline",
      accessor: (r: any) => fmt(r.bidDeadlineUtc),
    },
    {
      key: "createdDate", title: "Created",
      accessor: (r: any) => fmt(r.createdDate),
    },
    {
      key: "actions", title: "Actions",
      render: (r: any) => (
        <GroupButton items={[
          { id: "detail", icon: MdVisibility, color: "#2563EB", onClick: () => setDetailId(r.id) },
          ...(r.status === 1 ? [
            {
              id: "approve", icon: MdCheck, color: "#16A34A",
              onClick: async () => {
                if (window.confirm(`Approve tender "${r.title}"?`)) {
                  await api.adminTender.approve(r.id);
                  load();
                }
              },
            },
            {
              id: "reject", icon: MdClose, color: "#DC2626",
              onClick: () => setReasonModal({ id: r.id, type: "reject" }),
            },
            {
              id: "return", icon: MdUndo, color: "#F59E0B",
              onClick: () => setReasonModal({ id: r.id, type: "return" }),
            },
          ] : []),
        ]} />
      ),
    },
  ], [api, load]);

  return (
    <div style={{ padding: 20 }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 20, fontWeight: 700 }}>Tender Management</div>
        <div style={{ fontSize: 13, color: "#64748B" }}>
          All tenders across modules — view details, approve, reject, return
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
          <StatCard label="Total" value={stats.totalTenders} color="#6B7280" />
          <StatCard label="Pending Admin" value={stats.pendingAdminTenders} color="#F59E0B" />
          <StatCard label="Open" value={stats.openTenders} color="#16A34A" />
          <StatCard label="Shortlisting" value={stats.shortlistingTenders} color="#0EA5E9" />
          <StatCard label="Awarded" value={stats.awardedTenders} color="#7C3AED" />
          <StatCard label="Closed" value={stats.closedTenders} color="#374151" />
          <StatCard label="Cancelled" value={stats.cancelledTenders} color="#DC2626" />
        </div>
      )}

      {/* Filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={{ height: 36, borderRadius: 8, border: "1px solid #D1D5DB", padding: "0 10px", fontSize: 13, minWidth: 150 }}
        >
          <option value="">All Statuses</option>
          {Object.entries(TENDER_STATUS).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>

        <select
          value={filterModule}
          onChange={(e) => setFilterModule(e.target.value)}
          style={{ height: 36, borderRadius: 8, border: "1px solid #D1D5DB", padding: "0 10px", fontSize: 13, minWidth: 180 }}
        >
          <option value="">All Modules</option>
          {moduleOptions.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>

        {(filterStatus !== "" || filterModule !== "") && (
          <Button icon={MdClose} variant="ghost" onClick={() => { setFilterStatus(""); setFilterModule(""); }}>
            Clear
          </Button>
        )}
      </div>

      <Table loading={loading} columns={columns} data={filtered} />

      {detailId !== null && (
        <TenderDetailModal id={detailId} onClose={() => setDetailId(null)} />
      )}

      {reasonModal && (
        <ReasonModal
          title={reasonModal.type === "reject" ? "Reject Tender" : "Return Tender"}
          onClose={() => setReasonModal(null)}
          onSubmit={async (reason) => {
            if (reasonModal.type === "reject") {
              await api.adminTender.reject(reasonModal.id, reason);
            } else {
              await api.adminTender.returnTender(reasonModal.id, reason);
            }
            setReasonModal(null);
            load();
          }}
        />
      )}
    </div>
  );
};
